// Issue Submission Modal — in-app bug report form
// Follows ReleaseNotesModal pattern: Backdrop, ModalContainer, focus trap, theme tokens
// SPDX-License-Identifier: MIT

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { captureEnvironment, formatEnvironmentMarkdown } from '../../utils/environment-capture';

// --- Types ---

interface IssueSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SubmissionResult {
  success: boolean;
  issueUrl?: string;
  issueNumber?: number;
  error?: string;
}

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

// --- Category options ---
// Maps to GitHub organization issue types: Bug, Feature, Task
// See: mcp_github_list_issue_types for integralanalytics org

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report', ghType: 'Bug' },
  { value: 'feature', label: 'Feature Request', ghType: 'Feature' },
  { value: 'task', label: 'Task / Other', ghType: 'Task' },
] as const;

// --- Styled Components ---

const Backdrop = styled.div.withConfig({
  shouldForwardProp: prop => !['$isOpen'].includes(prop),
})<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1000;
  display: ${props => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  animation: ${props => (props.$isOpen ? 'fadeIn 0.2s ease-out' : 'none')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div.withConfig({
  shouldForwardProp: prop => !['$isOpen'].includes(prop),
})<{ $isOpen: boolean }>`
  width: 90%;
  max-width: 560px;
  max-height: 85vh;
  background: ${props => props.theme.sidePanelBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.borderColor}60;
  box-shadow:
    0 20px 40px 0 rgba(0, 0, 0, 0.3),
    0 8px 16px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${props => (props.$isOpen ? 'slideUp 0.3s cubic-bezier(0.1, 0.9, 0.2, 1)' : 'none')};

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 24px 28px 20px 28px;
  background: ${props => props.theme.sidePanelHeaderBg}F0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.theme.borderColor}40;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.titleTextColor};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.subtextColor};
  font-size: 28px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.15s cubic-bezier(0.1, 0.9, 0.2, 1);
  outline: none;
  line-height: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background: ${props => props.theme.panelBackgroundHover};
    color: ${props => props.theme.textColorHl};
    transform: scale(1.05);
  }
  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}60;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  background: ${props => props.theme.sidePanelBg};

  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.borderColor}60;
    border-radius: 6px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.primaryColor}80;
    background-clip: content-box;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.textColor};
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.borderColor}60;
  border-radius: 6px;
  color: ${props => props.theme.textColor};
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}30;
  }

  &::placeholder {
    color: ${props => props.theme.subtextColor};
    opacity: 0.6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px 14px;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.borderColor}60;
  border-radius: 6px;
  color: ${props => props.theme.textColor};
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s ease;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}30;
  }

  &::placeholder {
    color: ${props => props.theme.subtextColor};
    opacity: 0.6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.borderColor}60;
  border-radius: 6px;
  color: ${props => props.theme.textColor};
  font-size: 14px;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  appearance: auto;
  box-sizing: border-box;

  &:focus {
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}30;
  }
`;

const CharCount = styled.span.withConfig({
  shouldForwardProp: prop => !['$isOver'].includes(prop),
})<{ $isOver: boolean }>`
  font-size: 11px;
  color: ${props => (props.$isOver ? props.theme.errorColor : props.theme.subtextColor)};
  float: right;
  margin-top: 4px;
`;

const ModalFooter = styled.div`
  padding: 16px 28px 20px 28px;
  background: ${props => props.theme.sidePanelHeaderBg}F0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid ${props => props.theme.borderColor}40;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const SubmitButton = styled.button.withConfig({
  shouldForwardProp: prop => !['$isSubmitting'].includes(prop),
})<{ $isSubmitting?: boolean }>`
  padding: 10px 24px;
  background: ${props => props.theme.primaryBtnBgd};
  color: ${props => props.theme.primaryBtnColor};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 120px;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryBtnBgdHover};
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}60;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  color: ${props => props.theme.subtextColor};
  border: 1px solid ${props => props.theme.borderColor}60;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.panelBackgroundHover};
    color: ${props => props.theme.textColor};
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}60;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 24px;
  color: ${props => props.theme.textColor};

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: ${props => props.theme.titleTextColor};
  }

  p {
    font-size: 14px;
    color: ${props => props.theme.subtextColor};
    margin: 0 0 16px 0;
    line-height: 1.5;
  }
`;

const IssueLink = styled.a`
  color: ${props => props.theme.primaryColor};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: 2px solid ${props => props.theme.primaryColor}60;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ErrorBanner = styled.div`
  padding: 12px 14px;
  background: ${props => props.theme.errorBackground || 'rgba(255, 69, 58, 0.1)'};
  border: 1px solid ${props => props.theme.errorColor || '#ff453a'}30;
  border-radius: 6px;
  color: ${props => props.theme.errorColor || '#ff453a'};
  font-size: 13px;
  margin-bottom: 16px;
`;

// --- Component ---

const IssueSubmissionModal: React.FC<IssueSubmissionModalProps> = ({ isOpen, onClose }) => {
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');

  // Submission state
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Focus trap
  const focusTrapRef = useFocusTrap(isOpen);

  // Reset form when closing
  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setCategory('bug');
    setSubmissionState('idle');
    setResult(null);
    setErrorMessage('');
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    if (submissionState === 'submitting') return; // Don't close during submission
    resetForm();
    onClose();
  }, [submissionState, resetForm, onClose]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, handleClose]);

  // Validate form
  const isValid = title.trim().length > 0 && description.trim().length > 0;
  const isSubmitting = submissionState === 'submitting';

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    try {
      // Step 1: Capture environment
      const env = captureEnvironment();
      const envMarkdown = formatEnvironmentMarkdown(env);

      // Step 2: Submit to API
      setSubmissionState('submitting');

      const selectedCategory = CATEGORIES.find(c => c.value === category);
      const issueType = selectedCategory?.ghType || 'Task';

      const payload = {
        title: title.trim(),
        body: description.trim(),
        issueType,
        environment: envMarkdown,
      };

      const response = await fetch('/api/submit-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data);
        setSubmissionState('success');
      } else {
        setErrorMessage(data.error || 'Failed to submit issue. Please try again.');
        setSubmissionState('error');
      }
    } catch {
      setErrorMessage('Network error. Please check your connection and try again.');
      setSubmissionState('error');
    }
  };

  if (!isOpen) return null;

  // Get submit button text based on state
  const getSubmitText = () => {
    switch (submissionState) {
      case 'submitting':
        return 'Submitting…';
      default:
        return 'Submit Issue';
    }
  };

  return (
    <Backdrop $isOpen={isOpen} data-testid='issue-form-backdrop'>
      <ModalContainer
        ref={focusTrapRef}
        $isOpen={isOpen}
        role='dialog'
        aria-modal='true'
        aria-labelledby='issue-form-title'
        data-testid='issue-form-modal'
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id='issue-form-title'>Report a Bug</ModalTitle>
          <CloseButton onClick={handleClose} aria-label='Close issue form' disabled={isSubmitting}>
            ×
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {submissionState === 'success' && result ? (
            <SuccessMessage data-testid='issue-form-success'>
              <h3>Issue Submitted!</h3>
              <p>Your issue has been created successfully.</p>
              <IssueLink
                href={result.issueUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`View issue #${result.issueNumber} on GitHub`}
              >
                View Issue #{result.issueNumber} on GitHub →
              </IssueLink>
            </SuccessMessage>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {submissionState === 'error' && errorMessage && (
                <ErrorBanner role='alert' data-testid='issue-form-error'>
                  {errorMessage}
                </ErrorBanner>
              )}

              <FormGroup>
                <Label htmlFor='issue-title'>
                  Title <span aria-hidden='true'>*</span>
                </Label>
                <Input
                  id='issue-title'
                  type='text'
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder='Brief description of the issue'
                  maxLength={200}
                  required
                  disabled={isSubmitting}
                  aria-required='true'
                  aria-describedby='title-char-count'
                />
                <CharCount id='title-char-count' $isOver={title.length > 200}>
                  {title.length}/200
                </CharCount>
              </FormGroup>

              <FormGroup>
                <Label htmlFor='issue-description'>
                  Description <span aria-hidden='true'>*</span>
                </Label>
                <TextArea
                  id='issue-description'
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder='Describe the issue in detail. What happened? What did you expect?'
                  maxLength={5000}
                  required
                  disabled={isSubmitting}
                  aria-required='true'
                  aria-describedby='desc-char-count'
                />
                <CharCount id='desc-char-count' $isOver={description.length > 5000}>
                  {description.length}/5000
                </CharCount>
              </FormGroup>

              <FormGroup>
                <Label htmlFor='issue-category'>Category</Label>
                <Select
                  id='issue-category'
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  disabled={isSubmitting}
                  aria-label='Issue category'
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </form>
          )}
        </ModalContent>

        <ModalFooter>
          {submissionState === 'success' ? (
            <SubmitButton onClick={handleClose}>Close</SubmitButton>
          ) : (
            <>
              <CancelButton onClick={handleClose} disabled={isSubmitting} type='button'>
                Cancel
              </CancelButton>
              <SubmitButton
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                $isSubmitting={isSubmitting}
                type='submit'
                data-testid='issue-form-submit'
              >
                {getSubmitText()}
              </SubmitButton>
            </>
          )}
        </ModalFooter>
      </ModalContainer>
    </Backdrop>
  );
};

export default IssueSubmissionModal;
